import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { Slider } from '../../../components/ui/slider';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import QualityTierPicker from './QualityTierPicker';
import GenerationProgress from './GenerationProgress';
import CreativePreviewCard from './CreativePreviewCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STYLES = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'lifestyle', label: 'Lifestyle' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'indoor', label: 'Indoor' },
  { value: 'abstract', label: 'Abstract' }
];

const MOODS = [
  { value: 'professional', label: 'Professional', color: 'bg-blue-900' },
  { value: 'energetic', label: 'Energetic', color: 'bg-orange-600' },
  { value: 'minimal', label: 'Minimal', color: 'bg-gray-600' },
  { value: 'bold', label: 'Bold', color: 'bg-red-600' }
];

function FreshModeForm({ onJobStart }) {
  const [formData, setFormData] = useState({
    style: '',
    campaignBrief: '',
    mood: '',
    qualityTier: 'standard',
    quantity: 1,
    variantCount: 1,
    isBatch: false
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [generatedResults, setGeneratedResults] = useState(null);

  const calculateEstimatedCost = () => {
    const costPerImage = {
      draft: 0.003,
      standard: 0.025,
      premium: 0.05
    };
    return (costPerImage[formData.qualityTier] * formData.quantity).toFixed(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.style) {
      toast.error('Style is required');
      return;
    }
    if (!formData.campaignBrief.trim()) {
      toast.error('Campaign brief is required');
      return;
    }
    if (!formData.mood) {
      toast.error('Mood is required');
      return;
    }

    try {
      setIsGenerating(true);

      const response = await axios.post(`${API}/creative/generate-fresh`, {
        style: formData.style,
        campaign_brief: formData.campaignBrief,
        mood: formData.mood,
        quality_tier: formData.qualityTier,
        quantity: formData.quantity
      });

      setCurrentJobId(response.data.job_id);
      setGeneratedResults(null);
      // Hand the job up to the page so the completion toast survives tab switches.
      onJobStart?.(response.data.job_id);
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.error || 'Failed to start generation');
      setIsGenerating(false);
    }
  };

  const handleGenerationComplete = (result) => {
    setGeneratedResults(result);
    setCurrentJobId(null);
    setIsGenerating(false);
    // Terminal toast is fired by the page-level useCreativeJobPoll hook.
  };

  const handleGenerationError = (error) => {
    // Terminal toast is fired by the page-level useCreativeJobPoll hook.
    // Surface timeouts (which the hook doesn't toast) so the user isn't left hanging.
    if (error && /timeout/i.test(error)) {
      toast.error(error);
    }
    setCurrentJobId(null);
    setIsGenerating(false);
  };

  if (currentJobId) {
    return (
      <div className="max-w-2xl">
        <GenerationProgress
          jobId={currentJobId}
          qualityTier={formData.qualityTier}
          onComplete={handleGenerationComplete}
          onError={handleGenerationError}
        />
      </div>
    );
  }

  if (generatedResults) {
    // Handle both single image URL and array of URLs
    const imageUrls = generatedResults.imageUrls || (generatedResults.imageUrl ? [generatedResults.imageUrl] : []);

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {imageUrls.map((imageUrl, idx) => (
            <CreativePreviewCard
              key={idx}
              imageUrl={imageUrl}
              mode="Fresh"
              tier={formData.qualityTier}
              createdAt={new Date().toISOString()}
            />
          ))}
        </div>
        <Button
          onClick={() => setGeneratedResults(null)}
          variant="outline"
          className="border-white/10"
        >
          Generate Another Batch
        </Button>
      </div>
    );
  }

  return (
    <Card className="card max-w-2xl">
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Style */}
          <div>
            <Label htmlFor="style" className="text-white mb-2">Style *</Label>
            <Select value={formData.style} onValueChange={(value) => setFormData(prev => ({ ...prev, style: value }))}>
              <SelectTrigger className="bg-white/5 border-white/10">
                <SelectValue placeholder="Select a style" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-white/10">
                {STYLES.map(style => (
                  <SelectItem key={style.value} value={style.value}>{style.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Campaign Brief */}
          <div>
            <Label htmlFor="campaignBrief" className="text-white mb-2">Campaign Brief *</Label>
            <Textarea
              id="campaignBrief"
              value={formData.campaignBrief}
              onChange={(e) => setFormData(prev => ({ ...prev, campaignBrief: e.target.value }))}
              placeholder="Describe your campaign goals, theme, and any specific details..."
              className="bg-white/5 border-white/10 resize-none"
              rows={4}
            />
          </div>

          {/* Mood */}
          <div>
            <Label className="text-white mb-3 block">Mood *</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {MOODS.map(moodOption => (
                <button
                  key={moodOption.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, mood: moodOption.value }))}
                  className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                    formData.mood === moodOption.value
                      ? 'border-[#00d4aa] bg-[#00d4aa]/10'
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full ${moodOption.color}`} />
                  <span className="text-sm text-white">{moodOption.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <Label className="text-white">Quantity</Label>
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  isBatch: !prev.isBatch,
                  quantity: !prev.isBatch ? 2 : 1
                }))}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                  formData.isBatch
                    ? 'bg-[#00d4aa] text-black'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {formData.isBatch ? 'Batch' : 'Single'}
              </button>
            </div>

            {formData.isBatch ? (
              <div className="space-y-3">
                <Slider
                  value={[formData.quantity]}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, quantity: value[0] }))}
                  min={2}
                  max={10}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-white/60">
                  <span>{formData.quantity} images</span>
                  <span className="text-green-400 font-medium">~${calculateEstimatedCost()}</span>
                </div>
              </div>
            ) : (
              <div className="text-sm text-white/60">
                Estimated cost: <span className="text-green-400 font-medium">~${calculateEstimatedCost()}</span>
              </div>
            )}
          </div>

          {/* Quality Tier */}
          <div>
            <Label className="text-white mb-4 block">Quality Tier</Label>
            <QualityTierPicker
              value={formData.qualityTier}
              onChange={(tier) => setFormData(prev => ({ ...prev, qualityTier: tier }))}
              variantCount={formData.variantCount}
              onVariantCountChange={(count) => setFormData(prev => ({ ...prev, variantCount: count }))}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isGenerating}
            className="w-full bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-medium"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              `Generate ${formData.quantity} ${formData.quantity === 1 ? 'Image' : 'Images'}`
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default FreshModeForm;
